import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { DocumentTextIcon, LockClosedIcon, CheckCircleIcon, SearchIcon } from '@heroicons/react/outline';
import BupaLetterWizard from './BupaLetterWizard';
import SembleMdtReport from './SembleMdtReport';
import { useNavigate } from 'react-router-dom';
import PatientReportWizard from './PatientReportWizard';
import DownloadDropdown from './DownloadDropdown';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { TriggerFetchLatestDocForDownload } from '../../../../utils/TriggerFetchLatestDocForDownload';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { checkReportLogicVersion } from '../../../../utils/featureFlagUtils';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { getAdditionalDocumentsPanelTitle, isStaleEarlyExitPatientReport } from '../documentsDashboardHelpers';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { Tooltip } from 'react-tooltip';
import { getRequiredCoreSections, areAllRequiredSectionsApproved } from '../../../../utils/requiredSectionsUtils';
import { determinePatientReportType } from '../../../../utils/reportTypeUtils';
import { isAdhdAssessmentFlow } from '../../../../utils/conditionChecks';
import { TriggerPatientReportGeneration } from '../../../../utils/TriggerPatientReportGeneration';
import { getRenderCfName } from '../../../../utils/autoPatientReport';
import { getReportProcessingErrorMessage } from '../../../../utils/reportErrorMessages';

/**
 * AdditionalDocuments
 * -------------------
 * Renders the Patient Report and (optionally) BUPA Letter rows below the
 * core section rows on the Documents Dashboard.
 *
 * Processing statuses (prProcessingStatus, bupaProcessingStatus) are received
 * as props from the parent DocumentsDashboard, which maintains a single
 * consolidated onSnapshot listener.  This eliminates a redundant listener on
 * the same Firestore document and prevents concurrent fetchPatientData races.
 */
export default function AdditionalDocuments({
  patientId,
  clinicianId,
  prProcessingStatus = 'idle',
  bupaProcessingStatus = 'idle',
  onPreview,
}) {
  const navigate = useNavigate();
  const [showBupaLetterDialog, setShowBupaLetterDialog] = useState(false);
  const [showPatientReportDialog, setShowPatientReportDialog] = useState(false);
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const { patientDataModel } = useContext(PatientDataContext);

  // State for feature flag
  const [reportLogicVersion, setReportLogicVersion] = useState(1);

  // Show the timestamp when the Patient Report PDF was last produced.
  // We use prReportGeneratedAt (set by the CF on success) rather than
  // prReportApprovedAt (set at trigger time, before the CF runs).
  const prLastUpdatedAt = patientDataModel?.dataModel?.['6_manageReports']?.prReportGeneratedAt || null;

  // Check feature flag when component mounts
  useEffect(() => {
    const checkFeatureFlag = async () => {
      if (!patientId) return;
      
      try {
        const version = await checkReportLogicVersion(patientId);
        setReportLogicVersion(version);
      } catch (error) {
        console.error('Error checking feature flag:', error);
        setReportLogicVersion(1); // Default to old logic
      } finally {
      }
    };

    checkFeatureFlag();
  }, [patientId]);

  /**
   * UI Styling Helper Functions
   * --------------------------
   * These functions determine the visual presentation of document rows based on their status.
   * The functions check both the document approval status and processing state to apply 
   * appropriate styling.
   */

  // Helper function for document row background color
  const getBackgroundClass = (doc) => {
    // Get processing status for this document
    const docProcessingStatus = processingStatuses[doc.id] || 'idle';
    
    // For documents with special processing states
    if (docProcessingStatus === 'processing') {
      return 'bg-amber-50 hover:bg-amber-100';
    }
    if (docProcessingStatus === 'error') {
      return 'bg-red-50 hover:bg-red-100';
    }
    
    // For all documents based on approval status
    if (doc.status === 'approved') {
      return 'bg-green-50 hover:bg-green-100';
    }
    if (doc.status === 'ready-for-review') {
      return 'bg-violet-50 hover:bg-violet-100';
    }
    
    // Default styling
    return 'bg-gray-50 hover:bg-gray-100';
  };

  // Helper function for status circle styling
  const getCircleClass = (doc) => {
    // Get processing status for this document
    const docProcessingStatus = processingStatuses[doc.id] || 'idle';
    
    // For documents with special processing states
    if (docProcessingStatus === 'processing') {
      return 'bg-amber-50 border-amber-200 text-amber-700';
    }
    if (docProcessingStatus === 'error') {
      return 'bg-red-50 border-red-200 text-red-700';
    }
    
    // Standard status colors
    if (doc.status === 'approved') {
      return 'bg-green-50 border-green-200 text-green-700';
    }
    if (doc.status === 'ready-for-review') {
      return 'bg-violet-50 border-violet-200 text-violet-700';
    }
    
    return 'bg-gray-50 border-gray-200 text-gray-700';
  };

  // Add state for BUPA letter visibility
  const [showBupaLetter, setShowBupaLetter] = useState(false);

  // State for patient report type selection
  const [patientReportType, setPatientReportType] = useState("Patient Report - ADHD");

  // States for download operations
  const [isPatientReportDownloading, setIsPatientReportDownloading] = useState(false);
  const [isBupaLetterDownloading, setIsBupaLetterDownloading] = useState(false);

  // Processing statuses are now received from the parent Dashboard's
  // consolidated onSnapshot listener.  The values are already debounced
  // (transitions out of 'processing' are delayed 500ms) so the UI won't
  // briefly flash green during auto-helper retry cycles.
  const processingStatuses = useMemo(() => ({
    patientReport: prProcessingStatus,
    bupaLetter: bupaProcessingStatus,
  }), [prProcessingStatus, bupaProcessingStatus]);

  // Get claims from useAuth hook - no token refresh needed, always fresh.
  // Placed here (before fetchAdditionalDocsStatus) so `claims` is defined
  // before it is referenced in the useCallback dependency array.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Workflow-aware ADHD flow check: uses PPD for hierarchical DF orgs and
  // DD outcome for flat DF orgs.  Gates BUPA Letter visibility so it only
  // appears for patients on an ADHD assessment path (not for all flat DF
  // patients by default).  Computed internally rather than passed as a prop
  // because AdditionalDocuments already has all the required data.
  const isAdhdFlow = useMemo(
    () => isAdhdAssessmentFlow(patientDataModel, wf),
    [patientDataModel, wf]
  );

  /**
   * Data Fetching and Document Status Management
   * -------------------------------------------
   * This function retrieves the status of additional documents (Patient Report and BUPA Letter)
   * and determines their availability based on the patient's diagnosis and other reports.
   * 
   * It is wrapped in useCallback to prevent unnecessary re-creation during renders,
   * which would otherwise cause the useEffects depending on it to re-run.
   */
  const fetchAdditionalDocsStatus = useCallback(async () => {
    try {
      // Get fresh data from context instead of relying on stale prop
      const freshManageReports = patientDataModel?.dataModel?.['6_manageReports'] || {};
      
      const getStatus = (reportType) => {
        const isGenerated = freshManageReports[`${reportType}ReportGenerated`];
        const isApproved = freshManageReports[`${reportType}ReportApproved`];

        if (isApproved) {
          return 'approved';
        } else if (isGenerated) {
          return 'ready-for-review';
        } else {
          return 'locked';
        }
      };

      // Get the assigned clinician information
      const assignedClinicians = patientDataModel?.clinicians || {};

      // Check if CI report is ready or approved to determine if BUPA letter should be available
      const ciStatus = getStatus('ci');
      const isCICompleted = ciStatus === 'ready-for-review' || ciStatus === 'approved';

      // Determine the report type via the shared utility.  This handles
      // hierarchical vs flat DF models, SSI-always orgs, and DD outcome logic
      // in a single place — see reportTypeUtils.js for full documentation.
      const reportTypeToUse = determinePatientReportType(patientDataModel, claims?.organisation);
      setPatientReportType(reportTypeToUse);

      // Whether the Patient Report can be unlocked is gated by all required
      // core sections being approved.  The required set is workflow-dependent
      // (ADHD flow, SSI-always, standard) and comes from the shared utility.
      const requiredSections = getRequiredCoreSections(patientDataModel, claims?.organisation);
      const areAllReportsApproved = areAllRequiredSectionsApproved(freshManageReports, requiredSections);

      const ssiStatus = getStatus('ssi');
      const ddStatus = getStatus('dd');
      const padStatus = getStatus('pad');

      // Create documents array based on organization
      const documents = [
        {
          id: 'patientReport',
          title: 'Patient Report',
          order: 1,
          status: getStatus('pr'),
          description: 'Generate a comprehensive patient assessment report',
          containsAIOutputs: false,
          reviewer: {
            name: assignedClinicians.clinicianLead?.clinicianName || 'Unassigned',
            id: assignedClinicians.clinicianLead?.clinicianId || 'Unassigned'
          },
          areAllReportsApproved: areAllReportsApproved,
          reportStatuses: {
            ci: ciStatus,
            ssi: ssiStatus,
            dd: ddStatus,
            pad: padStatus
          }
        }
      ];

      // Only add BUPA Letter if the org has BUPA enabled and the patient is
      // on an ADHD assessment path (PPD for hierarchical, DD outcome for flat).
      if (showBupaLetter && isAdhdFlow) {
        documents.push({
          id: 'bupaLetter',
          title: 'BUPA Letter',
          order: 2,
          status: getStatus('bupaLetter'),
          description: 'Generate a formatted letter for Bupa insurance',
          containsAIOutputs: false,
          reviewer: {
            name: assignedClinicians.clinicianCI?.clinicianName || 'Unassigned',
            id: assignedClinicians.clinicianCI?.clinicianId || 'Unassigned'
          },
          ciStatus: ciStatus,
          isCICompleted: isCICompleted
        });
      }

      setAdditionalDocs(documents);
    } catch (error) {
      console.error("Error fetching additional documents status:", error);
      // Set default status if fetch fails
      const defaultDocuments = [
        {
          id: 'patientReport',
          title: 'Patient Report',
          order: 1,
          status: 'locked',
          description: 'Generate a comprehensive patient assessment report',
          containsAIOutputs: false,
          reviewer: {
            name: 'Unassigned'
          },
          areAllReportsApproved: false,
          reportStatuses: {
            ci: 'locked',
            ssi: 'locked',
            dd: 'locked',
            pad: 'locked'
          }
        }
      ];

      // Only add BUPA Letter if the org has BUPA enabled and the patient is
      // on an ADHD assessment path.
      if (showBupaLetter && isAdhdFlow) {
        defaultDocuments.push({
          id: 'bupaLetter',
          title: 'BUPA Letter',
          order: 2,
          status: 'locked',
          description: 'Generate a formatted letter for Bupa insurance',
          containsAIOutputs: false,
          reviewer: {
            name: 'Unassigned'
          },
          ciStatus: 'locked',
          isCICompleted: false
        });
      }

      setAdditionalDocs(defaultDocuments);
    }
  }, [patientDataModel, showBupaLetter, isAdhdFlow, claims?.organisation]);

  // BUPA Letter visibility: driven by the patient's workflow config
  useEffect(() => {
    if (!claims?.organisation) {
      setShowBupaLetter(false);
      return;
    }

    setShowBupaLetter(wf.isBupaLetterEnabled);
  }, [claims?.organisation, wf.isBupaLetterEnabled]);

  // Fetch additional documents status from Firestore
  useEffect(() => {    
    fetchAdditionalDocsStatus();
  }, [fetchAdditionalDocsStatus]);

  /**
   * Status Icon Renderer
   * ------------------
   * This function returns the appropriate status icon based on document status
   * and processing state. For documents being processed, it shows a spinner with
   * "Finalising..." text instead of the standard status icon.
   */
  const getStatusIcon = (doc) => {
    // Get processing status for this document
    const docProcessingStatus = processingStatuses[doc.id] || 'idle';
    
    // Show processing spinner instead of status icon for any document being processed
    if (docProcessingStatus === 'processing') {
      const tooltipId = doc.id === 'patientReport' ? `pr-processing-${patientId}` : undefined;
      return (
        <>
          <div className="flex items-center gap-2 text-amber-600" data-tooltip-id={tooltipId}>
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">Finalising...</span>
          </div>
          {doc.id === 'patientReport' && (
            <Tooltip
              id={tooltipId}
              place="top"
              style={{
                maxWidth: '250px',
                whiteSpace: 'normal',
                backgroundColor: 'rgba(34, 34, 34, 1)',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: 'bold'
              }}
              content="This can take a few minutes. You can navigate away from this page and come back later if needed."
            />
          )}
        </>
      );
    }
    
    // Show error indicator. For the Patient Report we surface a specific,
    // actionable message when the failure was caused by a missing/invalid
    // Contract Type, so the clinician knows exactly what to fix.
    if (docProcessingStatus === 'error') {
      const errorReason =
        patientDataModel?.dataModel?.['6_manageReports']?.prReportProcessingError;
      // Detail (outstanding paragraphs by section) is present for the
      // approval-gate 'unapproved_content' case; the message helper folds it
      // into an actionable summary and ignores it for other reasons.
      const errorDetail =
        patientDataModel?.dataModel?.['6_manageReports']?.prReportProcessingErrorDetail;
      const errorMessage =
        doc.id === 'patientReport'
          ? getReportProcessingErrorMessage(errorReason, errorDetail)
          : 'An error occurred. Please try again.';
      return (
        <div className="flex items-center gap-2 text-red-600">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{errorMessage}</span>
        </div>
      );
    }
    
    // Regular status icons based on document status
    switch (doc.status) {
      case 'locked':
        return <LockClosedIcon className="w-5 h-5 text-gray-400" />;
      case 'ready-for-review':
        return <DocumentTextIcon className="w-5 h-5 text-violet-600" />;
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  // Handle button click based on document status
  const handleDocAction = (docId, status, action) => {
    if (docId === 'bupaLetter') {
      if (action === 'download') {
        // Download the approved BUPA Letter
        handleDownloadBupaLetter();
      } else {
        // Open wizard for (re)generation
        setShowBupaLetterDialog(true);
      }
    } else if (docId === 'patientReport') {
      if (action === 'review') {
        // Navigate to review page for Patient Report
        navigate(`/patient/${patientId}/review-document-pr`);
      } else if (action === 'download') {
        // Download the approved Patient Report
        handleDownloadPatientReport();
      } else {
        // Open wizard for (re)generation
        setShowPatientReportDialog(true);
      }
    }
  };

  // Handle Patient Report download
  const handleDownloadPatientReport = async (format) => {
    try {
      setIsPatientReportDownloading(true);
      // setDownloadError(null);

      // Use the existing utility to trigger the download
      const result = await TriggerFetchLatestDocForDownload({
        reportStage: 'Patient',
        clinicianId,
        patientId,
        fileFormat: format
      });

      if (!result.success) {
        console.error('Download failed:', result.error);
        // setDownloadError(result.error || 'Failed to download document');

        // Show error in UI (you could add a toast or alert component here)
        alert('Failed to download document: ' + (result.error || 'Unknown error'));
      } else {
        console.log('Download initiated successfully');
      }
    } catch (error) {
      console.error('Error initiating download:', error);
      // setDownloadError(error.message || 'An unexpected error occurred');

      // Show error in UI
      alert('Error downloading document: ' + (error.message || 'Unknown error'));
    } finally {
      setIsPatientReportDownloading(false);
    }
  };

  // Handle BUPA Letter download
  const handleDownloadBupaLetter = async (format) => {
    try {
      setIsBupaLetterDownloading(true);
      // setDownloadError(null);

      // Use the existing utility to trigger the download
      const result = await TriggerFetchLatestDocForDownload({
        reportStage: 'BUPA Letter',
        clinicianId,
        patientId,
        fileFormat: format
      });

      if (!result.success) {
        console.error('Download failed:', result.error);
        // setDownloadError(result.error || 'Failed to download document');

        // Show error in UI (you could add a toast or alert component here)
        alert('Failed to download document: ' + (result.error || 'Unknown error'));
      } else {
        console.log('Download initiated successfully');
      }
    } catch (error) {
      console.error('Error initiating download:', error);
      // setDownloadError(error.message || 'An unexpected error occurred');

      // Show error in UI
      alert('Error downloading document: ' + (error.message || 'Unknown error'));
    } finally {
      setIsBupaLetterDownloading(false);
    }
  };

  return (
    <>
      <div className="my-4 max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border transition-all duration-200">
        <div className="ml-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {getAdditionalDocumentsPanelTitle(wf.replaceCARwithReportBuilder)}
          </h1>
        </div>

        {/* Document Rows - Styled like the main dashboard */}
        <div className="relative">
          {/* Vertical connecting line */}
          <div className="absolute left-[40px] top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {/* Document Sections Stack */}
          <div className="">
            {additionalDocs.map((doc) => {
              // Detect a Patient Report left over from an early-exit phase
              // (assessment ended after CI) that is now stale because the
              // assessment was resumed.  Drives the amber warning line and
              // allows Download/Preview to remain visible for the old version.
              const assessmentEnd = patientDataModel?.assessmentEnd;
              const isStaleEarlyExitPR = isStaleEarlyExitPatientReport(doc, assessmentEnd, wf);

              return (
              <div key={doc.id} className="relative">
                <div
                  className={`
                    w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200
                    ${getBackgroundClass(doc)}
                  `}
                >
                  {/* Circle with number */}
                  <div className={`
                    relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 
                    flex items-center justify-center font-semibold text-lg
                    ${getCircleClass(doc)}
                  `}>
                    {doc.order}
                  </div>

                  {/* Document Info */}
                  <div className="flex-1 flex items-center justify-between">
                    <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                      {getStatusIcon(doc)}
                    </div>

                      {/* Subtitle: reviewer / timestamp info, or stale early-exit warning.
                          When the assessment was resumed after an early exit, the Patient
                          Report from the early-exit phase is still on record but only covers
                          the Clinical Interview.  Show a "Previous version" line with an
                          amber warning instead of the normal "Approved by" / "Last updated"
                          subtitle.  Matches the CypAdhd stale-report pattern
                          (DocumentsDashboard_CypAdhd.js lines 1115-1121). */}
                      <div className="flex flex-col gap-1 mt-1">
                        {isStaleEarlyExitPR ? (
                          <>
                            <p className="text-sm italic text-gray-500">
                              Previous version from {assessmentEnd?.endedAt ? formatDateTime(assessmentEnd.endedAt) : 'earlier assessment'}
                            </p>
                            <p className="flex items-center gap-1 text-xs font-medium text-amber-700">
                              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                              Assessment resumed — this report only covers the Clinical Interview. It will update once all sections are approved.
                            </p>
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            {/* Special case for version 2 patient reports - show last updated timestamp.
                                Guard against null/pending timestamps (e.g. serverTimestamp() sentinel
                                hasn't resolved yet) by only rendering when prReportApprovedAt is truthy;
                                otherwise fall through to the regular "Approved by" display. */}
                            {(doc.id === 'patientReport' && reportLogicVersion === 2 && doc.status === 'approved' && prLastUpdatedAt) ? (
                              <p className="text-sm italic text-gray-700">
                                Last updated on {formatDateTime(prLastUpdatedAt)}
                              </p>
                            ) : (
                              <>
                                <p className="text-sm italic text-gray-700">
                                  {doc.status === 'approved'
                                    ? (doc.containsAIOutputs
                                      ? 'AI outputs reviewed and approved by'
                                      : 'Approved by')
                                    : doc.status === 'ready-for-review'
                                      ? (doc.containsAIOutputs
                                        ? 'AI outputs ready for review by'
                                        : 'Ready for review by')
                                      : 'Assigned to'}
                                </p>
                                <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                  <span>{doc.reviewer.name}</span>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Button - Different conditions for each document */}
                    <div className="flex items-center gap-3">
                      {doc.id === 'bupaLetter' ? (
                        // BUPA Letter button logic
                        <>
                          {/* Only show when not in processing state */}
                          {processingStatuses.bupaLetter !== 'processing' && (
                            <>
                              {/* Generation buttons - only for assigned reviewer */}
                              {doc.reviewer.id === clinicianId && (
                                doc.status === 'locked' ? (
                                  doc.isCICompleted ? (
                                    <button
                                      onClick={() => handleDocAction(doc.id, doc.status)}
                                      className="bg-violet-800 text-white hover:bg-violet-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                    >
                                      Create BUPA Letter
                                    </button>
                                  ) : (
                                    <div className="text-xs text-gray-500 italic">
                                      Available after Clinical Interview is completed
                                    </div>
                                  )
                                ) : (
                                  <button
                                    onClick={() => handleDocAction(doc.id, doc.status)}
                                    className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                  >
                                    Re-create BUPA Letter
                                  </button>
                                )
                              )}

                              {/* Download button - visible to all users when document is available */}
                              {doc.status !== 'locked' && (
                                <>
                                  <DownloadDropdown 
                                    onDownload={handleDownloadBupaLetter}
                                    isDownloading={isBupaLetterDownloading}
                                    label="Download Letter"
                                  />

                                  {/* Preview button — opens the rendered PDF in the
                                      parent Dashboard's embedded dialog via callback.
                                      Visible to all users (read-only). */}
                                  {onPreview && (
                                    <button
                                      onClick={() => {
                                        const bupaLastGen = patientDataModel?.documents?.bupaletterReport?.lastGenerated || null;
                                        onPreview('BUPA Letter', bupaLastGen);
                                      }}
                                      className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                    >
                                      <SearchIcon className="w-4 h-4 mr-1.5" />
                                      Preview
                                    </button>
                                  )}
                                </>
                              )}
                            </>
                          )}

                          {/* Error retry button */}
                          {processingStatuses.bupaLetter === 'error' && (
                            <button
                              onClick={() => handleDocAction(doc.id, doc.status)}
                              className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              Try Again
                            </button>
                          )}

                        </>
                      ) : doc.id === 'patientReport' ? (
                        // Patient Report button logic
                        <>
                          {/* Only show when not in processing state */}
                          {processingStatuses.patientReport !== 'processing' && (
                            <>
                              {/* Generation/Review buttons - different logic for version 1 vs version 2 */}
                              {doc.reviewer.id === clinicianId && (
                                reportLogicVersion === 2 ? (
                                  // Version 2: Auto-generated workflow
                                  doc.status === 'locked' ? (
                                    <div className="text-xs text-gray-500 italic">
                                       Available after all sections of the Clinical Assessment Report are approved
                                    </div>
                                  ) : null
                                ) : (
                                  // Version 1: Original workflow with manual generation and review
                                  doc.status === 'locked' ? (
                                    doc.areAllReportsApproved ? (
                                      <button
                                        onClick={() => handleDocAction(doc.id, doc.status)}
                                        className="bg-violet-800 text-white hover:bg-violet-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                      >
                                        Create Patient Report
                                      </button>
                                    ) : (
                                      <div className="text-xs text-gray-500 italic">
                                        Available after all sections of the Clinical Assessment Report are approved
                                      </div>
                                    )
                                  ) : (
                                    // Generated state - show generation and review buttons
                                    <>                              
                                      <button
                                        onClick={() => handleDocAction(doc.id, doc.status, 'regenerate')}
                                        className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                      >
                                        Re-generate
                                      </button>

                                      <button
                                        onClick={() => handleDocAction(doc.id, doc.status, 'review')}
                                        className={`
                                          flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                                          ${doc.status === 'ready-for-review'
                                            ? 'bg-violet-800 text-white hover:bg-violet-700'
                                            : 'text-gray-900 border border-gray-300 hover:bg-gray-100'
                                          }
                                        `}
                                      >
                                        {doc.status === 'ready-for-review' ? 'Review AI Outputs' : 'Review Paragraphs'}
                                      </button>
                                    </>
                                  )
                                )
                              )}
                            </>
                          )}

                          {/* Error retry button — for v2 patients, re-trigger
                              the auto-generation CF chain directly instead of
                              navigating to the v1-only RD_PR review page. */}
                          {processingStatuses.patientReport === 'error' && (
                            <button
                              onClick={() => {
                                if (reportLogicVersion === 2) {
                                  const reportType = determinePatientReportType(patientDataModel, claims?.organisation);
                                  TriggerPatientReportGeneration({ clinicianId, patientId, reportType, renderCfName: getRenderCfName(patientDataModel) });
                                } else {
                                  handleDocAction(doc.id, doc.status, 'review');
                                }
                              }}
                              className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              Try Again
                            </button>
                          )}

                          {/* Download button - visible to all users when report is
                              approved and not processing. Also shown when the report
                              is a stale early-exit version (may be ready-for-review)
                              so clinicians can still access the previous report while
                              the full assessment is in progress. */}
                          {((doc.status === 'approved' || isStaleEarlyExitPR) && processingStatuses.patientReport !== 'processing') && (
                            <>
                              {/* Tooltip shown on Download/Preview when the report is
                                  a stale early-exit version.  Provides audit context
                                  (date + clinician name) matching the PAD staleness
                                  tooltip pattern. */}
                              {isStaleEarlyExitPR && (
                                <Tooltip
                                  id={`stale-early-exit-pr-${patientId}`}
                                  place="top"
                                  style={{
                                    maxWidth: '280px',
                                    whiteSpace: 'normal',
                                    backgroundColor: 'rgba(34, 34, 34, 1)',
                                    borderRadius: '10px',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                  }}
                                  content={`Generated on ${assessmentEnd?.endedAt ? formatDateTime(assessmentEnd.endedAt) : 'an earlier date'} when ${assessmentEnd?.endedByName || 'a clinician'} ended the assessment early after the Clinical Interview.`}
                                />
                              )}
                              <span data-tooltip-id={isStaleEarlyExitPR ? `stale-early-exit-pr-${patientId}` : undefined}>
                                <DownloadDropdown
                                  onDownload={handleDownloadPatientReport}
                                  isDownloading={isPatientReportDownloading}
                                  label="Download Report"
                                />
                              </span>

                              {/* Preview button — opens the rendered PDF in the
                                  parent Dashboard's embedded dialog via callback.
                                  Visible to all users (read-only). */}
                              {onPreview && (
                                <button
                                  data-tooltip-id={isStaleEarlyExitPR ? `stale-early-exit-pr-${patientId}` : undefined}
                                  onClick={() => {
                                    const prLastGen = patientDataModel?.documents?.patientReport?.lastGenerated || prLastUpdatedAt;
                                    onPreview('Patient', prLastGen);
                                  }}
                                  className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                >
                                  <SearchIcon className="w-4 h-4 mr-1.5" />
                                  Preview
                                </button>
                              )}
                            </>
                          )}

                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ); })}

            {/* Semble MDT Report — Well Life Clinic only. Rendered inside the
                Additional Documents panel as another row, below the Patient
                Report. */}
            {wf.isSembleMdtReportEnabled && (
              <SembleMdtReport
                patientId={patientId}
                clinicianId={clinicianId}
                onPreview={onPreview}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bupa Letter Wizard Dialog */}
      <BupaLetterWizard
        open={showBupaLetterDialog}
        onOpenChange={setShowBupaLetterDialog}
        patientId={patientId}
        clinicianId={clinicianId}
      />

      {/* Patient Report Wizard Dialog */}
      <PatientReportWizard
        open={showPatientReportDialog}
        onOpenChange={setShowPatientReportDialog}
        patientId={patientId}
        clinicianId={clinicianId}
        reportType={patientReportType}
      />
    </>
  );
}